import { PAL } from '@hudhuria/shared';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { RingLogo, Wordmark } from '~/components/ui';
import { entryPath } from '~/lib/entry';
import { session } from '~/session/session';

export const Route = createFileRoute('/welcome')({ component: Onboarding });

const STEPS = [
  {
    title: 'Attendance in seconds, not minutes',
    body: 'Scan an ID card or tap a name and the register writes itself — across every center in the park.',
  },
  {
    title: 'Groups or walk-ins, one tap each',
    body: 'Roll-call a whole class or workshop at once, or check in a single visitor and note what they came for.',
  },
  {
    title: 'Works online or off',
    body: 'No signal at the gate? Keep checking people in. Everything syncs the moment you reconnect.',
  },
];

function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const finish = () => {
    session.setOnboarded();
    void navigate({ to: entryPath(), replace: true });
  };
  const next = () => (step === 2 ? finish() : setStep(step + 1));
  const s = STEPS[step]!;

  return (
    <div className="flex flex-1 flex-col px-[26px] pt-safe-6 pb-safe">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <RingLogo size={26} />
          <Wordmark />
        </div>
        <button
          type="button"
          onClick={finish}
          className="cursor-pointer border-none bg-transparent text-[14px] font-semibold text-slate-500"
        >
          Skip
        </button>
      </div>

      <div className="flex flex-1 flex-col justify-center">
        <div key={step}>
          {/* decorative mock-ups with sample names, so screen readers skip them */}
          <div aria-hidden="true">
            {step === 0 && <RingIllustration />}
            {step === 1 && <FacesIllustration />}
            {step === 2 && <SyncIllustration />}
          </div>
          <div className="mt-[34px]">
            <div
              className="font-display text-[30px] leading-[1.1] font-bold tracking-[-0.02em] text-pretty"
              style={{ animation: 'rise .55s ease .1s both' }}
            >
              {s.title}
            </div>
            <div
              className="mt-3 text-[15px] leading-[1.55] text-pretty text-slate-500"
              style={{ animation: 'rise .55s ease .22s both' }}
            >
              {s.body}
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex gap-1.5">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-[7px] rounded-[4px]"
              style={{
                width: i === step ? 22 : 7,
                background: i === step ? '#0369a1' : '#d6dee8',
                transition: 'all .35s cubic-bezier(.3,1.4,.5,1)',
              }}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={next}
          className="cursor-pointer rounded-[14px] border-none bg-brand px-[26px] py-[15px] text-[15px] font-semibold text-white"
          style={{ animation: step === 2 ? 'ctaPulse 1.8s ease-out 1.2s infinite' : 'none' }}
        >
          {step === 2 ? 'Get started' : 'Next'}
        </button>
      </div>
    </div>
  );
}

function RingIllustration() {
  return (
    <div className="relative flex h-[290px] items-center justify-center">
      {[1.6, 2.8].map((d) => (
        <div
          key={d}
          className="absolute top-1/2 left-1/2 -mt-[115px] -ml-[115px] h-[230px] w-[230px] rounded-full border-2 border-sky-200"
          style={{ opacity: 0, animation: `ripple 2.4s ease-out ${d}s infinite` }}
        />
      ))}
      <div
        className="relative flex h-[230px] w-[230px] items-center justify-center rounded-full"
        style={{
          background: 'conic-gradient(#38bdf8 calc(var(--p) * 1%), #e6f4fc 0)',
          boxShadow: '0 18px 44px rgba(3,105,161,0.2)',
          animation:
            'ringFill 1.6s cubic-bezier(.2,.8,.2,1) .25s both, pop .6s cubic-bezier(.3,1.4,.5,1) both',
        }}
      >
        <div className="flex h-[178px] w-[178px] flex-col items-center justify-center rounded-full bg-brand text-white">
          <span
            className="count-38 font-display text-[60px] leading-[0.9] font-bold"
            style={{
              counterReset: 'n var(--n)',
              animation: 'countUp 1.6s cubic-bezier(.2,.8,.2,1) .25s both',
            }}
          />
          <div
            className="mt-1.5 text-[13px]"
            style={{ opacity: 0, animation: 'fadeInK .4s ease 1.6s forwards' }}
          >
            in · 4 seconds
          </div>
        </div>
      </div>
      <div
        className="absolute right-0.5 bottom-1.5 flex items-center gap-2 rounded-[14px] border border-line bg-white px-[11px] py-2 shadow-[0_10px_24px_rgba(15,23,42,0.1)]"
        style={{ opacity: 0, animation: 'chipIn .5s cubic-bezier(.3,1.4,.5,1) 1.8s forwards' }}
      >
        <div className="flex h-[26px] w-[26px] items-center justify-center rounded-full bg-amber-500 text-[10.5px] font-bold text-white">
          AD
        </div>
        <span className="text-[12px] font-semibold">Amara checked in</span>
        <div className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-emerald-500 text-[10px] text-white">
          ✓
        </div>
      </div>
    </div>
  );
}

const FACES = ['AD', 'LC', 'ZO', 'NK', 'MP', 'OS', 'EB', 'KM'];

function FacesIllustration() {
  return (
    <div className="flex h-[290px] flex-col items-center justify-center gap-4">
      <div
        className="grid grid-cols-[repeat(4,52px)] gap-[10px] rounded-[24px] bg-[#f3f8fc] p-[18px]"
        style={{ animation: 'rise .45s ease both' }}
      >
        {FACES.map((i, k) => {
          const on = !(k === 2 || k === 6);
          return (
            <div
              key={i}
              className="relative flex h-[52px] w-[52px] items-center justify-center rounded-full text-[14px] font-bold"
              style={{
                background: on ? PAL[k % PAL.length] : '#e2e8f0',
                color: on ? '#fff' : '#94a3b8',
                transform: 'scale(0)',
                animation: `pop .45s cubic-bezier(.3,1.6,.5,1) ${(0.15 + k * 0.07).toFixed(2)}s forwards`,
              }}
            >
              {i}
              {on && (
                <div
                  className="absolute -right-0.5 -bottom-0.5 flex h-[18px] w-[18px] items-center justify-center rounded-full border-2 border-[#f3f8fc] bg-emerald-500 text-[9px] text-white"
                  style={{
                    transform: 'scale(0)',
                    animation: `pop .35s cubic-bezier(.3,1.6,.5,1) ${(0.95 + k * 0.11).toFixed(2)}s forwards`,
                  }}
                >
                  ✓
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div
        className="flex items-center gap-[10px] rounded-2xl border border-line bg-white px-3 py-[10px] shadow-[0_8px_20px_rgba(15,23,42,0.07)]"
        style={{ opacity: 0, animation: 'chipIn .5s cubic-bezier(.3,1.4,.5,1) 1.9s forwards' }}
      >
        <div className="flex h-[34px] w-[34px] items-center justify-center rounded-full bg-amber-500 text-[12px] font-bold text-white">
          KM
        </div>
        <div className="text-[13px] font-semibold">Kofi · walk-in</div>
        <div
          className="relative h-[30px] w-[78px] overflow-hidden rounded-[9px] bg-brand"
          style={{ animation: 'toGreen .3s ease 2.7s forwards' }}
        >
          <div
            className="absolute inset-0 flex items-center justify-center text-[12px] font-bold text-white"
            style={{ animation: 'fadeOutK .2s ease 2.7s forwards' }}
          >
            Check in
          </div>
          <div
            className="absolute inset-0 flex items-center justify-center text-[12px] font-bold text-white"
            style={{ opacity: 0, animation: 'fadeInK .25s ease 2.85s forwards' }}
          >
            ✓ In
          </div>
        </div>
      </div>
    </div>
  );
}

function SyncIllustration() {
  const row =
    'flex items-center justify-between rounded-xl bg-white px-3 py-[10px] text-[12.5px] font-semibold';
  return (
    <div className="flex h-[290px] flex-col items-center justify-center gap-[14px]">
      <div className="flex w-[236px] flex-col gap-[9px] rounded-[22px] bg-[#f3f8fc] p-4">
        <div className={row} style={{ opacity: 0, animation: 'slideIn .45s ease .2s forwards' }}>
          <span>Amara · 9:02</span>
          <span className="text-brand">Saved</span>
        </div>
        <div className={row} style={{ opacity: 0, animation: 'slideIn .45s ease .45s forwards' }}>
          <span>Noah · 9:08</span>
          <span className="text-brand">Saved</span>
        </div>
        <div className={row} style={{ opacity: 0, animation: 'slideIn .45s ease .7s forwards' }}>
          <span>Group · 12 in</span>
          <span className="relative h-4 w-[52px] text-right">
            <span
              className="absolute right-0 text-amber-600"
              style={{ animation: 'fadeOutK .25s ease 2.5s forwards' }}
            >
              Queued
            </span>
            <span
              className="absolute right-0 text-brand"
              style={{ opacity: 0, animation: 'fadeInK .3s ease 2.7s forwards' }}
            >
              Saved
            </span>
          </span>
        </div>
      </div>
      <div className="relative flex h-8 w-[236px] justify-center">
        <div
          className="flex items-center gap-2 rounded-[20px] bg-brand-50 px-[13px] py-[7px] text-[12.5px] font-semibold text-brand"
          style={{ opacity: 0, animation: 'fadeInK .35s ease 1s forwards' }}
        >
          <span
            className="h-[7px] w-[7px] rounded-full bg-amber-500"
            style={{ animation: 'toGreen .3s ease 2.5s forwards' }}
          />
          <span className="relative h-4 w-[168px]">
            <span
              className="absolute left-0 whitespace-nowrap"
              style={{ animation: 'fadeOutK .25s ease 2.5s forwards' }}
            >
              Offline · 1 waiting to sync
            </span>
            <span
              className="absolute left-0 whitespace-nowrap"
              style={{ opacity: 0, animation: 'fadeInK .3s ease 2.7s forwards' }}
            >
              Back online · all synced
            </span>
          </span>
        </div>
      </div>
    </div>
  );
}
